import { Menu, X } from "lucide-react";
import * as React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/features/i18n/language-switcher";
import { useLocale } from "@/features/i18n/locale-provider";
import type { TranslationKey } from "@/features/i18n/dictionary";
import { cn } from "@/lib/utils";

const links: Array<{ to: string; key: TranslationKey }> = [
  { to: "/villa", key: "nav.villa" },
  { to: "/story", key: "nav.story" },
  { to: "/experiences", key: "nav.experiences" },
  { to: "/gallery", key: "nav.gallery" },
  { to: "/contact", key: "nav.contact" },
];

export function SiteHeader({ brandName }: { brandName: string }) {
  const { t } = useLocale();
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  // A route change closes the overlay; without this, back/forward leaves it up.
  React.useEffect(() => setOpen(false), [location.pathname]);

  React.useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header className="relative z-30 border-b border-ink/10 bg-bg">
      <div className="mx-auto flex max-w-page items-center justify-between gap-4 px-5 py-4 sm:px-8 lg:px-16">
        <Link
          to="/"
          className="font-serif text-[clamp(1.25rem,2vw,1.625rem)] tracking-[0.01em] text-ink no-underline"
        >
          {brandName}
        </Link>

        <nav className="hidden items-center gap-4 lg:flex xl:gap-7" aria-label="Primary">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                cn(
                  "font-sans text-sm no-underline transition-colors",
                  isActive
                    ? "border-b border-accent pb-[3px] font-semibold text-ink"
                    : "text-ink-muted hover:text-ink",
                )
              }
            >
              {t(link.key)}
            </NavLink>
          ))}
          <LanguageSwitcher />
          <Button asChild size="sm" className="min-h-11 px-[1.375rem] text-sm">
            <Link to="/booking" className="no-underline">
              {t("cta.book")}
            </Link>
          </Button>
        </nav>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={t("nav.menu")}
            aria-expanded={open}
            className="flex size-11 items-center justify-center text-ink"
          >
            <Menu className="size-6" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {open && <MobileMenu onClose={() => setOpen(false)} brandName={brandName} />}
    </header>
  );
}

/** Overlay enters from the top with a 40ms stagger (brief §3.7). */
function MobileMenu({ onClose, brandName }: { onClose: () => void; brandName: string }) {
  const { t } = useLocale();
  const closeRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex animate-lift flex-col bg-bg lg:hidden"
      role="dialog"
      aria-modal="true"
      aria-label={t("nav.menu")}
    >
      <div className="flex items-center justify-between border-b border-ink/10 px-5 py-4">
        <span className="font-serif text-xl text-ink">{brandName}</span>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label={t("nav.close")}
          className="flex size-11 items-center justify-center text-ink"
        >
          <X className="size-6" strokeWidth={1.5} />
        </button>
      </div>

      <nav className="flex flex-1 flex-col gap-1 px-5 py-8" aria-label="Primary">
        {links.map((link, index) => (
          <NavLink
            key={link.to}
            to={link.to}
            onClick={onClose}
            style={{ animationDelay: `${index * 40}ms` }}
            className={({ isActive }) =>
              cn(
                "animate-rise py-3 font-serif text-h3 no-underline",
                isActive ? "text-accent" : "text-ink",
              )
            }
          >
            {t(link.key)}
          </NavLink>
        ))}
        <NavLink
          to="/faq"
          onClick={onClose}
          style={{ animationDelay: `${links.length * 40}ms` }}
          className="animate-rise py-3 font-serif text-h3 text-ink no-underline"
        >
          {t("nav.faq")}
        </NavLink>
      </nav>

      <div className="border-t border-ink/10 p-5">
        <Button asChild block size="lg">
          <Link to="/booking" onClick={onClose} className="no-underline">
            {t("cta.book")}
          </Link>
        </Button>
      </div>
    </div>
  );
}
